import React, { useEffect, useState } from "react";
import moment from "moment";
import { Container } from "reactstrap";
import { isNil } from "ramda";
import Alerts from "../../molecules/Alerts";
import { v4 } from "uuid";
import {
  apiGetRequest,
  apiReopenRequest,
  financeApproverDataLoader,
  lineManagerDataLoader,
  sendBackToEmployee,
  validateRequest,
  getMyHRStatus,
} from "../../../utils/queries";
import { SendBackToEmployeeModal } from "../History/SendBackToEmployeeModal";
import { RequestModal } from "../RequestModal/RequestModal";
import { ReopenModal } from "../History/ReopenModal";
import { ReminderModal } from "./ReminderModal";
import { ChangeApproverModel } from "../ChangeApproverModel/ChangeApproverModel";
import { RequestFilesForm } from "./RequestFilesForm";
import { trackPromise } from "react-promise-tracker";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBan,
  faBell,
  faCheckCircle,
  faClock,
  faFileAlt,
  faPaperclip,
  faPen,
  faUndo,
  faUniversity,
  faUpload,
  faUser,
  faEnvelopeOpenText,
} from "@fortawesome/free-solid-svg-icons";
import "./RequestDetailsPage.css";

const paymentTypes = {
  IBAN: "IBAN",
  SWIFT: "BIC",
  REGNOACCOUNT: "REGNO/ACCOUNT",
  ABARoutingNumber: "ABA ROUTING NUMBER",
};

const statusColorMap = {
  "Completed": "rdp-status--approved",
  "Form requested": "rdp-status--requested",
  "Form submitted": "rdp-status--requested",
  "Form resubmitted": "rdp-status--resubmitted",
  "Send back by requestor": "rdp-status--sendback",
  "Reopen": "rdp-status--sendback",
  "Validated by requestor": "rdp-status--resubmitted",
  "Cancelled": "rdp-status--rejected",
  "Rejected by line manager": "rdp-status--rejected",
  "Rejected by finance": "rdp-status--rejected",
  "Approved by line manager": "rdp-status--approved",
  "Approved by finance": "rdp-status--approved",
};

const SectionCard = ({ icon, title, badge, children }) => (
  <div className="rdp-section-card">
    <div className="rdp-section-card__header">
      <div className="rdp-section-card__title">
        {icon && <FontAwesomeIcon icon={icon} />}
        <span>{title}</span>
      </div>
      {badge != null && <span className="rdp-section-card__badge">{badge}</span>}
    </div>
    <div className="rdp-section-card__body">{children}</div>
  </div>
);

// Read-only label/value box; onEdit adds a pencil in the corner.
const bField = (label, value, onEdit) => (
  <div className="rdp-vbdm__field" key={label}>
    <div className="rdp-vbdm__field-label">{label}</div>
    <div className={`rdp-vbdm__field-value${!value ? " rdp-vbdm__field-value--empty" : ""}`}>
      {value || "—"}
    </div>
    {onEdit && (
      <button
        className="rdp-edit-btn"
        type="button"
        onClick={onEdit}
        title={`Change ${label.toLowerCase()}`}
        style={{ position: "absolute", top: 8, right: 8 }}
      >
        <FontAwesomeIcon icon={faPen} />
      </button>
    )}
  </div>
);

const fmt = (date) => (date ? moment(date).format("DD-MM-YYYY HH:mm") : "-");

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || error?.toString() || "An error occurred";

const RequestDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [formConfirmed, setFormConfirmed] = useState(false);
  const [banks, setBanks] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [myHRStatus, setMyHRStatus] = useState(null);
  const [toggleRequestModal, setToggleRequestModal] = useState(false);
  const [toggleSendBackToVendorModal, setToggleSendBackToVendorModal] = useState(false);
  const [toggleReopenRequestModal, setToggleReopenRequestModal] = useState(false);
  const [toggleReminderModal, setToggleReminderModal] = useState(false);
  const [toggleChangeApproverModel, setToggleChangeApproverModel] = useState(false);
  const [changeApprover, setChangeApprover] = useState({ type: "", dataLoader: null, defaultValue: "" });
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const addAlert = (message, color = "success") => {
    setAlerts((prev) => [...prev, { message, id: v4(), color }]);
  };

  const applyResponse = (data) => {
    const requestData = data.request || data;
    setRequest(requestData);
    setEmployee(requestData.employee || data.employee);
    setBanks(data.bankDataList);
    setFileList(data.files || []);
  };

  const loadRequest = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const result = await trackPromise(apiGetRequest(id));
      if (result.data.request?.verballyConfirmed || result.data.verballyConfirmed) {
        setFormConfirmed(true);
      }
      applyResponse(result.data);
    } catch (error) {
      addAlert(getErrorMessage(error), "danger");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
  }, [id]);

  useEffect(() => {
    if (request?.id) {
      getMyHRStatus(request.id)
        .then((result) => setMyHRStatus(result.data))
        .catch(() => {});
    }
  }, [request]);

  const canValidate = () => request?.status === "Form submitted" && formConfirmed;
  const canSendBack = () => request?.status === "Form submitted";
  const canReopen = () => request?.status === "Rejected by line manager";
  const canCancel = () =>
    request?.status !== "Cancelled" && request?.status !== "Validated by requestor";
  const canSendReminder = () =>
    [
      "Form requested",
      "Validated by requestor",
      "Send back by requestor",
      "Approved by line manager",
      "Reopen",
    ].includes(request?.status);

  const canChangeLineManager = () =>
    isNil(request?.approval?.approvedByLineManagerEmail) &&
    !["Rejected by line manager", "Cancelled", "Completed"].includes(request?.status);

  const canChangeFinance = () =>
    isNil(request?.approval?.approvedByFinanceEmail) &&
    isNil(request?.formResubmittedByEmail) &&
    !["Rejected by finance", "Cancelled", "Completed", "Form resubmitted"].includes(request?.status);

  const showChangeApproverModal = (type, dataLoader, defaultValue = "") => {
    setChangeApprover({ type, dataLoader, defaultValue });
    setToggleChangeApproverModel(true);
  };

  const onUpdateRequest = (response) => {
    setRequest(response.data.request ? response.data.request : response.data);
  };

  const onValidate = async () => {
    try {
      await validateRequest(id);
      await loadRequest({ silent: true });
      addAlert("Successfully validated the request");
    } catch (error) {
      addAlert(getErrorMessage(error), "danger");
    }
  };

  const onReminderSent = (data) => {
    setRequest((prev) => ({ ...prev, sendReminderDate: data.sendReminderDate }));
    addAlert("Reminder sent successfully");
  };

  const onSendBackToEmployee = async (requestId, reason) => {
    try {
      const result = await sendBackToEmployee(requestId, reason);
      setRequest(result.data.request);
      addAlert("Successfully resubmitted the request");
    } catch (error) {
      addAlert(getErrorMessage(error), "danger");
    }
  };

  const onReopen = async (requestId, reason) => {
    try {
      const result = await apiReopenRequest(requestId, reason);
      applyResponse(result.data);
      addAlert("Form reopened successfully");
    } catch (error) {
      addAlert(getErrorMessage(error), "danger");
    }
  };

  const renderBankFields = (bankData) => {
    const type = bankData?.type;
    const accountNr = bankData?.accountNr ?? bankData?.bankAccountNumber;
    const currency = bankData?.currency?.currencyCode;
    let fields;

    if (type === paymentTypes.IBAN) {
      fields = [
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Currency", currency],
        ["IBAN", bankData?.iban],
        ["BIC/SWIFT", bankData?.bic],
        ["Bank", bankData?.bank],
        ["Branch", bankData?.branch],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Country Code", bankData?.countryCode],
      ];
    } else if (type === paymentTypes.SWIFT) {
      fields = [
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Currency", currency],
        ["Account number", accountNr],
        ["BIC/SWIFT", bankData?.bic],
        ["Corresponding BIC/SWIFT", bankData?.correspondingBIC],
        ...(bankData?.regNo ? [["Registration Number", bankData.regNo]] : []),
        ["Bank", bankData?.bank],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Country Code", bankData?.countryCode],
      ];
    } else if (type === paymentTypes.REGNOACCOUNT) {
      fields = [
        ["Registration Number", bankData?.regNo],
        ["Account number", accountNr],
        ["IBAN", bankData?.iban],
        ["Currency", currency || "DKK"],
        ["BIC/SWIFT", bankData?.bic],
        ["Bank", bankData?.bank],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country || "Denmark"],
      ];
    } else if (type === paymentTypes.ABARoutingNumber) {
      fields = [
        ["ABA Routing Number", bankData?.abaRoutingNumber],
        ["Bank Account Number", bankData?.bankAccountNumber],
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Account Type", bankData?.accountType],
        ["Bank", bankData?.bank],
        ["Branch", bankData?.branch],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Currency", currency],
      ];
    } else {
      fields = [
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Currency", currency],
        ["IBAN", bankData?.iban],
        ["BIC/SWIFT", bankData?.bic],
        ["Corresponding BIC/SWIFT", bankData?.correspondingBIC],
        ["Registration Number", bankData?.regNo],
        ["Account number", accountNr],
        ["ABA Routing Number", bankData?.abaRoutingNumber],
        ["Bank", bankData?.bank],
        ["Branch", bankData?.branch],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Country Code", bankData?.countryCode],
      ].filter(([, value]) => value);
    }

    return [["Type", type], ["Purpose", bankData?.purpose], ...fields].map(([label, value]) =>
      bField(label, value)
    );
  };

  if (loading) {
    return <Container className="rdp-container" />;
  }

  if (!request) {
    return (
      <Container className="rdp-container">
        <button className="rdp-back-btn" onClick={() => navigate("/history")}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to History
        </button>
        <Alerts alerts={alerts} setAlerts={setAlerts} />
        <div className="rdp-loading">Request not found.</div>
      </Container>
    );
  }

  const approval = request.approval;
  const heroCls = statusColorMap[request.status] || "rdp-status--default";
  const employeeName = `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim();
  const editContactEnabled = canChangeLineManager();

  return (
    <Container className="rdp-container">
      <button className="rdp-back-btn" onClick={() => navigate("/history")}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to History
      </button>

      <Alerts alerts={alerts} setAlerts={setAlerts} />

      <div className="rdp-hero">
        <div className="rdp-hero__top">
          <div className="rdp-hero__identity">
            <div className="rdp-hero__avatar">{getInitials(employeeName)}</div>
            <div>
              <div className="rdp-hero__name">{employeeName || "-"}</div>
              <div className="rdp-hero__id">Request #{request.id}</div>
            </div>
          </div>
          <span className={"rdp-hero__status " + heroCls}>{request.status}</span>
        </div>

        <div className="rdp-hero__meta">
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Submitted</span>
            <span className="rdp-hero__meta-value">{fmt(request.formSubmittedDate)}</span>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Requestor</span>
            <span className="rdp-hero__meta-value">{request.requestedByName || "-"}</span>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Line Manager</span>
            <span className="rdp-hero__meta-value">{request.lineManagerToNotify || "-"}</span>
            {canChangeLineManager() && (
              <button
                className="rdp-edit-btn"
                type="button"
                title="Change line manager"
                onClick={() => showChangeApproverModal("Line manager", lineManagerDataLoader)}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
            )}
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Finance Approver</span>
            <span className="rdp-hero__meta-value">{request.financeApproverToNotify || "-"}</span>
            {canChangeFinance() && (
              <button
                className="rdp-edit-btn"
                type="button"
                title="Change finance approver"
                onClick={() => showChangeApproverModal("Finance approver", financeApproverDataLoader)}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
            )}
          </div>
        </div>

        <div className="rdp-hero__actions">
          <button className="rdp-btn rdp-btn--validate" onClick={onValidate} disabled={!canValidate()}>
            <FontAwesomeIcon icon={faCheckCircle} /> Validate
          </button>
          <button
            className="rdp-btn rdp-btn--cancel"
            onClick={() => setToggleRequestModal(true)}
            disabled={!canCancel()}
          >
            <FontAwesomeIcon icon={faBan} /> Cancel
          </button>
          <button
            className="rdp-btn rdp-btn--ghost-hero"
            onClick={() => setToggleReminderModal(true)}
            disabled={!canSendReminder()}
          >
            <FontAwesomeIcon icon={faBell} /> Send Reminder
          </button>
          <button
            className="rdp-btn rdp-btn--ghost-hero"
            onClick={() => setToggleSendBackToVendorModal(true)}
            disabled={!canSendBack()}
          >
            <FontAwesomeIcon icon={faEnvelopeOpenText} /> Send back to employee
          </button>
          <button
            className="rdp-btn rdp-btn--ghost-hero"
            onClick={() => setToggleReopenRequestModal(true)}
            disabled={!canReopen()}
          >
            <FontAwesomeIcon icon={faUndo} /> Reopen form
          </button>
        </div>
      </div>

      <div className="rdp-verbal-card">
        <span className="rdp-verbal-card__label">Employee bank details verbally confirmed</span>
        <label className={`rdp-toggle${request.verballyConfirmed ? " rdp-toggle--disabled" : ""}`}>
          <input
            type="checkbox"
            checked={formConfirmed}
            disabled={request.verballyConfirmed}
            onChange={() => setFormConfirmed(!formConfirmed)}
          />
          <span className="rdp-toggle__track">
            <span className="rdp-toggle__thumb" />
          </span>
        </label>
      </div>

      <SectionCard icon={faUser} title="Employee Information">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("First name", employee?.firstName)}
              {bField("Last name", employee?.lastName)}
              {bField("Private email", employee?.privateEmail)}
              {bField("Private phone", employee?.privatePhone)}
              {bField("Address Line 1", employee?.addressLine1)}
              {bField("Address Line 2", employee?.addressLine2)}
              {bField("City", employee?.city)}
              {bField("Zip/Postal", employee?.zip)}
              {bField("Country", employee?.country)}
              {bField("MyHR Id", employee?.myHRId)}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={faFileAlt} title="Request Information">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("Request ID", request.id)}
              {bField("Status", request.status)}
              {bField("Submitted", request.formSubmittedDate ? fmt(request.formSubmittedDate) : null)}
              {bField("Requestor", request.requestedByName)}
              {bField("Requestor e-mail", request.requestedByEmail)}
              {bField(
                "Contact email",
                request.requestContactEmail,
                editContactEnabled
                  ? () => showChangeApproverModal("Contact email", null, request.requestContactEmail)
                  : undefined
              )}
              {bField(
                "Contact mobile",
                request.requestContactMobile,
                editContactEnabled
                  ? () => showChangeApproverModal("Contact mobile", null, request.requestContactMobile)
                  : undefined
              )}
              {bField("Line Manager", request.lineManagerToNotify)}
              {bField("Finance Approver", request.financeApproverToNotify)}
              {request.additionalEmail && bField("Additional email", request.additionalEmail)}
              {request.myHRId && bField("MyHR ID", request.myHRId)}
              {approval?.approvedByLineManagerName && bField("Approved by Line Manager", approval.approvedByLineManagerName)}
              {approval?.approvedByLineManagerAt && bField("Approved by Line Manager date", fmt(approval.approvedByLineManagerAt))}
              {approval?.rejectedByLineManagerName && bField("Rejected by Line Manager", approval.rejectedByLineManagerName)}
              {approval?.rejectedByLineManagerName && bField("Rejected by Line Manager date", fmt(approval.rejectedByLineManagerAt))}
              {approval?.rejectedByLineManagerReason && bField("Line Manager rejection reason", approval.rejectedByLineManagerReason)}
              {approval?.approvedByFinanceName && bField("Approved by Finance", approval.approvedByFinanceName)}
              {approval?.approvedByFinanceAt && bField("Approved by Finance date", fmt(approval.approvedByFinanceAt))}
              {approval?.rejectedByFinanceName && bField("Rejected by Finance", approval.rejectedByFinanceName)}
              {approval?.rejectedByFinanceName && bField("Rejected by Finance date", fmt(approval.rejectedByFinanceAt))}
              {approval?.rejectedByFinanceReason && bField("Finance rejection reason", approval.rejectedByFinanceReason)}
              {request.status === "Cancelled" && request.cancelledBy && bField("Cancelled By", request.cancelledBy)}
              {request.status === "Cancelled" && request.cancelledByReason && bField("Cancellation Reason", request.cancelledByReason)}
              {fileList.length > 0 && (
                <div className="rdp-vbdm__field">
                  <div className="rdp-vbdm__field-label">Attachments</div>
                  <div className="rdp-vbdm__field-value">
                    {fileList.map((file, i) => (
                      <a
                        key={i}
                        className="rdp-file-item"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "flex", marginBottom: 4 }}
                      >
                        <FontAwesomeIcon icon={faPaperclip} /> {file.displayName}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={faUniversity} title="Bank Details" badge={banks?.length ?? 0}>
        <div className="rdp-vbdm__body">
          {!banks?.length && (
            <div className="rdp-vbdm__section">
              <div className="rdp-vbdm__field-value rdp-vbdm__field-value--empty">No bank details</div>
            </div>
          )}
          {banks?.map((bankData, idx) => (
            <React.Fragment key={bankData.id ?? idx}>
              {idx > 0 && <div className="rdp-vbdm__divider" />}
              <div className="rdp-vbdm__section">
                <div className="rdp-vbdm__grid">{renderBankFields(bankData)}</div>
                {bankData?.files?.length > 0 && (
                  <div className="rdp-vbdm__grid">
                    <div className="rdp-vbdm__field">
                      <div className="rdp-vbdm__field-label">File</div>
                      <div className="rdp-vbdm__field-value">
                        {bankData.files.map((file, fi) => (
                          <a
                            key={fi}
                            className="rdp-file-item"
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "flex", marginBottom: 4 }}
                          >
                            <FontAwesomeIcon icon={faFileAlt} /> {file.displayName}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={faClock} title="MyHR Status">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("Status", myHRStatus?.status)}
              {bField("Error message", myHRStatus?.errors?.length ? myHRStatus.errors.join("; ") : myHRStatus?.errorMessage)}
              {bField("Retry count", myHRStatus ? String(myHRStatus.retryCount ?? 0) : null)}
              {bField("Received date", myHRStatus?.receivedAt ? fmt(myHRStatus.receivedAt) : null)}
              {bField("Resolved date", myHRStatus?.resolvedAt ? fmt(myHRStatus.resolvedAt) : null)}
              {bField("Next retry date", myHRStatus?.nextRetryAt ? fmt(myHRStatus.nextRetryAt) : null)}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={faUpload}
        title="File Attachments"
        badge={fileList.length > 0 ? fileList.length : null}
      >
        <div className="rdp-file-attachments">
          <p className="rdp-upload-hint">Upload file as additional business justification.</p>
          <RequestFilesForm
            bankDataId={0}
            requestId={request.id}
            token={request.token}
            name="mainFileForm"
            fileList={fileList}
            setFileList={setFileList}
            addAlert={addAlert}
          />
        </div>
      </SectionCard>

      <RequestModal
        setToggle={setToggleRequestModal}
        toggle={toggleRequestModal}
        request={request}
        onUpdateRequest={onUpdateRequest}
      />
      <SendBackToEmployeeModal
        requestId={id}
        setToggle={setToggleSendBackToVendorModal}
        toggleValue={toggleSendBackToVendorModal}
        onSubmit={onSendBackToEmployee}
      />
      <ReopenModal
        addAlert={addAlert}
        setToggle={setToggleReopenRequestModal}
        toggle={toggleReopenRequestModal}
        onSubmit={onReopen}
        requestId={id}
      />
      <ReminderModal
        toggle={toggleReminderModal}
        setToggle={setToggleReminderModal}
        request={request}
        onSent={onReminderSent}
        onError={(error) => addAlert(getErrorMessage(error), "danger")}
      />
      <ChangeApproverModel
        account={request}
        onUpdateAccount={onUpdateRequest}
        setToggleModal={setToggleChangeApproverModel}
        toggleModal={toggleChangeApproverModel}
        type={changeApprover.type}
        dataLoader={changeApprover.dataLoader}
        defaultValue={changeApprover.defaultValue}
      />
    </Container>
  );
};

export default RequestDetailsPage;
